import { z } from "zod";

/**
 * Why a build that has not started yet is still waiting — COMPUTED ON READ,
 * never stored. Nothing about a queued build's row changes while it waits, so a
 * stored reason would describe the instant it was written and go stale the
 * moment a sibling build ends. The server derives it from the live in-flight
 * count and the current cap on every read.
 *
 *   - `org_at_capacity`: the organization already has `inFlight` builds admitted,
 *     starting or running, and may run at most `cap` at once. The build is
 *     admitted as soon as one of them ends. `inFlight` can exceed `cap` after
 *     an admin lowers the cap; builds already running are never stopped.
 *   - `region_at_capacity`: the organization builds on CodeBuild, and the
 *     platform already runs as many CodeBuild builds in its `region` as that
 *     region allows. The build is admitted as soon as one of them ends. The
 *     region's counts are NOT carried: they sum every tenant's builds there, and
 *     one tenant's read must not reveal how busy the others are. `ahead` is
 *     carried, because it counts only the organization's own builds.
 *   - `git_host_unavailable`: the app's code lives on the organization's own git
 *     host, and no live address to clone it from can be composed right now — the
 *     host is unhealthy or being replaced, or the repository has no recorded
 *     path. The build runner skips such a build, still queued and never failed,
 *     and admits it once the address returns. Named first, before any ceiling:
 *     the runner never reaches a ceiling for a build it skips. The backend sends
 *     no `ahead` on it — the build is not taken in its line's order while it
 *     waits on its host — but the field is declared like every variant's, since
 *     readers read `ahead` off the whole union.
 *     Emitted from the same verdict both dispatch paths withhold on
 *     (`isVibeGitRemoteAwaitingGitHost` over `resolveVibeGitRemote`), so this
 *     reason and the runner cannot disagree.
 *   - `cost_safety_throttled`: the organization is over its monthly soft limit,
 *     so the build runner meters how often it may START a build, and this
 *     dispatch window's budget is spent. The build stays queued — nothing is
 *     failed or torn down — and the org's next start is allowed once the window
 *     turns over, at `nextDispatchWindowAt`. That instant is the ORGANIZATION's,
 *     never a promised start for this build: the build may still have `ahead`
 *     of its own org's builds to wait behind. Named after every ceiling, as the
 *     runner meets them: the throttle is only consulted for a build no ceiling
 *     is holding. Emitted from the same lease, counter and policy the runner's
 *     admission reads, so this reason and the runner cannot disagree.
 *   - `queued`: nothing the platform counts is holding it back; the build
 *     runner has not picked it up yet.
 *
 * `ahead` is the build's place in line: how many of the SAME organization's
 * queued builds the claimer admits before this one, in the claimer's own
 * `(queuedAt, id)` order. Per organization on purpose — builds are admitted
 * fairly ACROSS organizations, so a place against other tenants depends on what
 * they do next and has no honest single number. Optional on the wire, because a
 * backend a release behind does not send it; a reader names no place then,
 * never a guessed one.
 *
 * `message` is the sentence to show a person, rendered as-is.
 */
const AheadSchema = z.number().int().nonnegative().optional();

export const VibeBuildWaitSchema = z.discriminatedUnion("reason", [
  z.object({
    reason: z.literal("org_at_capacity"),
    inFlight: z.number().int().nonnegative(),
    cap: z.number().int().positive(),
    ahead: AheadSchema,
    message: z.string()
  }),
  z.object({
    reason: z.literal("region_at_capacity"),
    region: z.string().min(1),
    ahead: AheadSchema,
    message: z.string()
  }),
  z.object({
    reason: z.literal("git_host_unavailable"),
    ahead: AheadSchema,
    message: z.string()
  }),
  z.object({
    reason: z.literal("cost_safety_throttled"),
    nextDispatchWindowAt: z.coerce.date(),
    ahead: AheadSchema,
    message: z.string()
  }),
  z.object({
    reason: z.literal("queued"),
    ahead: AheadSchema,
    message: z.string()
  })
]);

/**
 * The STRICT wait: what this backend is allowed to emit. Every reason it lists
 * has its own variant, so a reader switching on `reason` is told by the compiler
 * when the list grows. The backend's own emitter is typed as this and its spec
 * parses its output against it, so the read-side tolerance below never hides a
 * wait the backend built wrong.
 */
export type VibeBuildWaitDto = z.infer<typeof VibeBuildWaitSchema>;

/**
 * Every reason the strict union lists. A `Record` over the reason type, so a
 * variant added above is a compile error here until it is listed — the read
 * schema below must never treat a listed reason as unlisted.
 */
const LISTED_REASONS: Readonly<Record<VibeBuildWaitDto["reason"], true>> = {
  org_at_capacity: true,
  region_at_capacity: true,
  git_host_unavailable: true,
  cost_safety_throttled: true,
  queued: true
};

function isListedReason(reason: string): boolean {
  return Object.prototype.hasOwnProperty.call(LISTED_REASONS, reason);
}

/**
 * A wait whose reason this build of the contract does not list, normalised to
 * one variant a reader can switch on. The server's code is kept in `code`, never
 * as `reason`: a reader names it only through words it already has for it, and
 * otherwise renders `message` — the server's own sentence — or a generic one.
 *
 * Only the fields every wait shares are carried, each one lenient (`.catch`), so
 * a newer variant that drops or reshapes one still reads as waiting.
 */
const UnlistedWaitSchema = z
  .object({
    reason: z.string().refine((reason) => !isListedReason(reason)),
    message: z.string().catch(""),
    ahead: AheadSchema.catch(undefined)
  })
  .transform((wait): { reason: "unlisted"; code: string; message: string; ahead?: number } => ({
    reason: "unlisted",
    code: wait.reason,
    message: wait.message,
    // Optional keys stay ABSENT when the wire had none, as on the strict variants.
    ...(wait.ahead === undefined ? {} : { ahead: wait.ahead })
  }));

export type VibeBuildWaitUnlistedDto = z.infer<typeof UnlistedWaitSchema>;

/**
 * The wait as a CONSOLE reads it off a response: the strict variants, plus
 * `unlisted` for a reason this console's contract does not list yet.
 *
 * The backend and the console deploy separately, so a backend that has learned a
 * new reason serves it to a console that has not. Under the strict union alone
 * that one unknown string fails the WHOLE response parse in the generated hook —
 * the deploy-state read dies, not just its wait sentence. Here it degrades to
 * `unlisted` and the rest of the response reads normally.
 *
 * Tolerant of an unknown REASON only. A listed reason with a malformed body still
 * fails the parse: that is a backend bug, not version skew, and the strict
 * variant is what reports it.
 */
export const VibeBuildWaitReadSchema = z.union([VibeBuildWaitSchema, UnlistedWaitSchema]);

export type VibeBuildWaitReadDto = z.infer<typeof VibeBuildWaitReadSchema>;
